import { useParams } from 'react-router'
import { useStoreConfigurations } from '../../../api/store-configurations/useStoreConfigurations.hooks'
import { StoreConfigurationRelationshipEnum } from '../../../__codegen__/api'
import { useWarehouseContext } from '../../../utils/contexts/warehouse/WarehouseContext.hooks'
import { type StoreConfigurationScopedRouteParams } from '../../../legacy/common/types'
import { useStoreConfigIds } from '../../../utils/routing/params.hooks'
import { usePartnerContext } from '../../../utils/contexts/partner/PartnerContext.hooks'
import { ViewType } from './ScopeSelection.types'

interface RetailerSelectRequiredProps {
  selectionType?: ViewType
  retailerSelectionOptional?: boolean
  storeConfigSelectionOptional?: boolean
  hiddenScopePicker?: boolean
}

/**
 * Determines if a retailer selection is required based on the selectionType
 * and the currently selected retailer and storeConfig
 */
export const useIsRetailerSelectRequired = ({
  selectionType,
  retailerSelectionOptional,
  storeConfigSelectionOptional,
  hiddenScopePicker,
}: RetailerSelectRequiredProps) => {
  const { warehouse } = useWarehouseContext()
  const params = useParams<StoreConfigurationScopedRouteParams>()
  const storeConfigId = Number(params.storeConfigId)

  selectionType = selectionType || ViewType.RETAILER

  // if scope picker is optional, then override retailerSelectionOptional, storeConfigSelectionOptional
  retailerSelectionOptional ||= hiddenScopePicker
  storeConfigSelectionOptional ||= hiddenScopePicker

  const bannerSelected = retailerSelectionOptional || warehouse
  const siteSelected = storeConfigSelectionOptional || storeConfigId > 0

  const isRetailerSelectRequired = !(
    (selectionType == ViewType.RETAILER && bannerSelected) ||
    (selectionType == ViewType.RETAILER_AND_STORE_CONFIG && bannerSelected && siteSelected) ||
    (selectionType == ViewType.STORE_CONFIG && siteSelected)
  )

  return isRetailerSelectRequired
}

/**
 * Fetches storeConfiguration data, based on availableWarehouses
 * Requires PartnerContext to be defined
 */
export const useStoreConfigData = () => {
  const { warehouses: availableWarehouses } = usePartnerContext()
  const storeConfigIds = useStoreConfigIds({ throwOnMissing: false })
  const storeConfigurations = useStoreConfigurations({
    retailerIds: availableWarehouses?.map(e => e.id.toString()),
    storeConfigRelationship: StoreConfigurationRelationshipEnum.Associated,
  })

  const params = useParams<StoreConfigurationScopedRouteParams>()
  const storeConfigId = Number(params.storeConfigId)

  return {
    storeConfigId,
    storeConfigIds,
    storeConfigurations,
  }
}
